// frontend_react/src/pages/calendar/EditEvent.jsx

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

import AddressTypeahead from "../../components/AddressTypeahead";
import EventRegistrationEmailSender from "../../components/email/EventRegistrationEmailSender";

import {
  fetchEventById,
  createEvent,
  updateEvent,
  deleteEvent,
} from "../../services/calendarApi";

import envelopeIcon from "../../assets/envelope.svg";
import { useAdminScope } from "../../context/AdminScopeContext";
import { AdminButton } from "../admin/action_engine";
import {
  AdminErrorState,
  AdminSuccessState,
} from "../admin/ui_engine";
import { useAdminConfirmation } from "../admin/panel_engine/PanelServicesContext";
import {
  AdminActions,
  AdminSection,
  AdminSectionBadge,
  AdminSectionHeader,
  AdminSectionTitle,
} from "../admin/section_engine";

import {
  instantToEventLocalInput,
  instantToEventDateInput,
  eventLocalInputToApi,
  eventDateInputToApi,
  nextEventDateInput,
} from "./eventTemporal";

import {
  EVENT_CATEGORY_VALUES,
  EVENT_CATEGORY_PRESENTATION,
} from "./eventCategoryPresentation";

function EditEvent() {
  const { eventUuid: uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useAdminConfirmation();
  const {
    selectedCompanyUuid,
    selectedActivityUuid,
  } = useAdminScope();

  const isEditMode = Boolean(uuid) && uuid !== "create";
  const [createdUuid, setCreatedUuid] = useState("");
  const effectiveUuid = isEditMode ? uuid : createdUuid;

  const [form, setForm] = useState({
    title: "",
    description: "",
    url: "",
    startDate: location.state?.startDate || "",
    endDate: location.state?.endDate || "",
    address: "",
    recurrence_rule: "",
    is_all_day: false,
    timezone: "Europe/Paris",
    conference_provider: "",
    event_category: "other",
    max_participants: "",
    payment_required: false,
    payment_amount_ttc: "",
    payment_currency: "EUR",
    payment_provider: "",
    payment_url: "",
  });

  const [addressMeta, setAddressMeta] = useState({
    lat: null,
    lng: null,
    city: "",
    postal_code: "",
    country: "",
  });

  const [participants, setParticipants] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setAllDay = (checked) => {
    setForm((prev) => {
      if (checked) {
        const startDate = prev.startDate
          ? prev.startDate.slice(0, 10)
          : "";

        let endDate = prev.endDate
          ? prev.endDate.slice(0, 10)
          : "";

        if (startDate && (!endDate || endDate <= startDate)) {
          endDate = nextEventDateInput(startDate);
        }

        return {
          ...prev,
          is_all_day: true,
          startDate,
          endDate,
        };
      }

      return {
        ...prev,
        is_all_day: false,
        startDate: prev.startDate
          ? `${prev.startDate.slice(0, 10)}T00:00`
          : "",
        endDate: prev.endDate
          ? `${prev.endDate.slice(0, 10)}T00:00`
          : "",
      };
    });
  };

  const loadEventData = async () => {
    setError("");

    try {
      const { data } = await fetchEventById(uuid, {
        companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
      });
      const event = data?.event ?? data;

      setForm({
        title: event.title || "",
        description: event.description || "",
        url: event.url || "",
        startDate: event.is_all_day
          ? instantToEventDateInput(
              event.start_timestamp,
              event.timezone || "Europe/Paris"
            )
          : instantToEventLocalInput(
              event.start_timestamp,
              event.timezone || "Europe/Paris"
            ),
        endDate: event.is_all_day
          ? instantToEventDateInput(
              event.end_timestamp,
              event.timezone || "Europe/Paris"
            )
          : instantToEventLocalInput(
              event.end_timestamp,
              event.timezone || "Europe/Paris"
            ),
        address: event.location || "",
        recurrence_rule: event.recurrence_rule || "",
        is_all_day: Boolean(event.is_all_day),
        timezone: event.timezone || "Europe/Paris",
        conference_provider: event.conference_provider || "",
        event_category: event.event_category || "other",
        max_participants: event.max_participants || "",
        payment_required: Boolean(event.payment_required),
        payment_amount_ttc: event.payment_amount_ttc || "",
        payment_currency: event.payment_currency || "EUR",
        payment_provider: event.payment_provider || "",
        payment_url: event.payment_url || "",
      });

      const mappedParticipants = (event.participants || []).map((email) => ({
        name: email,
        status: "pending",
        mailed: true,
      }));

      setParticipants(mappedParticipants);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement de l’événement.");
    }
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description || null,
    url: form.url || null,
    location: form.address || null,
    start_timestamp: form.is_all_day
      ? eventDateInputToApi(form.startDate)
      : eventLocalInputToApi(form.startDate),
    end_timestamp: form.is_all_day
      ? eventDateInputToApi(form.endDate)
      : eventLocalInputToApi(form.endDate),
    recurrence_rule: form.recurrence_rule || null,
    is_all_day: Boolean(form.is_all_day),
    timezone: form.timezone || "Europe/Paris",
    conference_provider: form.conference_provider || null,
    event_category: form.event_category || "other",
    participants: participants.map((participant) => participant.name),
    max_participants: form.max_participants ? Number(form.max_participants) : null,
    payment_required: Boolean(form.payment_required),
    payment_amount_ttc:
      form.payment_required && form.payment_amount_ttc
        ? form.payment_amount_ttc
        : null,
    payment_currency: form.payment_currency || "EUR",
    payment_provider:
      form.payment_required && form.payment_provider
        ? form.payment_provider
        : null,
    payment_url:
      form.payment_required && form.payment_url
        ? form.payment_url
        : null,
  });

  const handleSaveEvent = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!form.title.trim()) {
      setError("Le titre est obligatoire.");
      return;
    }

    if (!form.startDate || !form.endDate) {
      setError("Les dates de début et de fin sont obligatoires.");
      return;
    }

    try {
      const payload = buildPayload();

      if (isEditMode) {
        await updateEvent(uuid, payload, {
          companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
        });
        setMessage("Événement mis à jour.");
      } else {
        const res = await createEvent(payload, {
          companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
        });
        const newUuid =
          res?.uuid ||
          res?.event_uuid ||
          res?.data?.uuid ||
          res?.data?.event_uuid;

        if (newUuid) setCreatedUuid(newUuid);
        setMessage("Événement créé.");
      }

      setTimeout(() => navigate("/calendar"), 900);
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la sauvegarde de l’événement.");
    }
  };

  const ensureEventUuid = async () => {
    if (isEditMode) return uuid;
    if (createdUuid) return createdUuid;

    if (!form.title.trim()) {
      throw new Error("Ajoute un titre avant d’envoyer le mail.");
    }

    if (!form.startDate) {
      throw new Error("Ajoute une date de début avant d’envoyer le mail.");
    }

    const res = await createEvent(buildPayload(), {
      companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
    });

    const newUuid =
      res?.uuid ||
      res?.event_uuid ||
      res?.data?.uuid ||
      res?.data?.event_uuid;

    if (!newUuid) {
      throw new Error("API createEvent : uuid manquant dans la réponse.");
    }

    setCreatedUuid(newUuid);
    return newUuid;
  };

  const handleDeleteEvent = async () => {
    const confirmed = await confirm({
      title: "Supprimer l’événement ?",
      message: "Confirmer la suppression de cet événement ?",
      confirmLabel: "Supprimer",
      cancelLabel: "Annuler",
      intent: "danger",
    });

    if (!confirmed) return;

    try {
      await deleteEvent(uuid, {
        companyUuid: selectedCompanyUuid,
        activityUuid: selectedActivityUuid,
      });
      navigate("/calendar");
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la suppression de l’événement.");
    }
  };

  const handleAddParticipant = () => {
    const email = inviteEmail.trim();

    if (!email) return;
    if (participants.some((participant) => participant.name === email)) {
      setError("Ce participant est déjà ajouté.");
      return;
    }

    setParticipants((prev) => [
      ...prev,
      { name: email, status: "pending", mailed: false },
    ]);

    setInviteEmail("");
    setError("");
  };

  const handleRemoveParticipant = (indexToRemove) => {
    setParticipants((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const nonInvitedEmails = useMemo(
    () =>
      participants
        .filter((participant) => participant?.name && participant.mailed === false)
        .map((participant) => participant.name),
    [participants]
  );

  const markMailed = (sentEmails) => {
    const sentSet = new Set((sentEmails || []).map((email) => email.toLowerCase()));

    setParticipants((prev) =>
      prev.map((participant) => {
        const key = (participant?.name || "").toLowerCase();
        return sentSet.has(key) ? { ...participant, mailed: true } : participant;
      })
    );
  };

  useEffect(() => {
    if (isEditMode) {
      loadEventData();
    }
  }, [
    uuid,
    isEditMode,
    selectedCompanyUuid,
    selectedActivityUuid,
  ]);

  return (
    <EditorWorkspace>
      <AdminSection>
        <AdminSectionHeader>
          <HeaderCopy>
            <AdminSectionBadge>Calendrier</AdminSectionBadge>
            <AdminSectionTitle>
              {isEditMode ? "Modifier l’événement" : "Créer un événement"}
            </AdminSectionTitle>
            <HeaderDescription>
              Paramètre les informations publiques de l’événement, les places
              disponibles, les options de paiement et les participants invités.
            </HeaderDescription>
          </HeaderCopy>
        </AdminSectionHeader>
      </AdminSection>

      {message && (
        <AdminSuccessState message={message} compact />
      )}

      {error && (
        <AdminErrorState message={error} compact />
      )}

      <FormWrapper onSubmit={handleSaveEvent}>
            <MainGrid>
              <LeftColumn>
                <AdminSection>
                  <AdminSectionHeader>
                    <AdminSectionBadge>Informations</AdminSectionBadge>
                    <AdminSectionTitle>Détails principaux</AdminSectionTitle>
                  </AdminSectionHeader>

                  <FieldsGrid>
                    <Field $full>
                      <label htmlFor="event-title">Titre *</label>
                      <input
                        id="event-title"
                        value={form.title}
                        onChange={(e) => setField("title", e.target.value)}
                        placeholder="Soirée jeux de société"
                        required
                      />
                    </Field>

                    <Field>
                      <label htmlFor="event-start">Début *</label>
                      <input
                        id="event-start"
                        type={form.is_all_day ? "date" : "datetime-local"}
                        value={form.startDate}
                        onChange={(e) => setField("startDate", e.target.value)}
                        required
                      />
                    </Field>

                    <Field>
                      <label htmlFor="event-end">Fin *</label>
                      <input
                        id="event-end"
                        type={form.is_all_day ? "date" : "datetime-local"}
                        value={form.endDate}
                        onChange={(e) => setField("endDate", e.target.value)}
                        required
                      />
                    </Field>

                    <CheckRow $full>
                      <input
                        type="checkbox"
                        checked={form.is_all_day}
                        onChange={(e) => setAllDay(e.target.checked)}
                      />
                      <span>Toute la journée</span>
                    </CheckRow>

                    <Field $full>
                      <label>Lieu</label>
                      <AddressTypeahead
                        value={form.address}
                        onChange={(value) => setField("address", value)}
                        onSelect={(data) => {
                          setField("address", data.formatted_address || "");
                          setAddressMeta({
                            lat: data.lat ?? null,
                            lng: data.lng ?? null,
                            city: data.city || "",
                            postal_code: data.postal_code || "",
                            country: data.country || "",
                          });
                        }}
                        placeholder="56 rue de Poissy, Saint-Germain-en-Laye"
                        inputStyle={{
                          width: "100%",
                          minHeight: "42px",
                          border: "none",
                          borderRadius: "10px",
                          padding: "0.75rem",
                          background: "#fff9ea",
                          color: "#0b1328",
                          fontWeight: 750,
                          boxSizing: "border-box",
                          outline: "none",
                        }}
                      />
                    </Field>

                    <Field $full>
                      <label htmlFor="event-url">Lien externe</label>
                      <input
                        id="event-url"
                        type="url"
                        value={form.url}
                        onChange={(e) => setField("url", e.target.value)}
                        placeholder="https://..."
                      />
                    </Field>

                    <Field $full>
                      <label htmlFor="event-description">Description</label>
                      <textarea
                        id="event-description"
                        value={form.description}
                        onChange={(e) => setField("description", e.target.value)}
                        placeholder="Description interne ou publique de l’événement..."
                      />
                    </Field>
                  </FieldsGrid>
                </AdminSection>

                <AdminSection>
                  <AdminSectionHeader>
                    <AdminSectionBadge>Paramètres</AdminSectionBadge>
                    <AdminSectionTitle>Récurrence, visio & capacité</AdminSectionTitle>
                  </AdminSectionHeader>

                  <FieldsGrid>
                    <Field>
                      <label htmlFor="event-timezone">Fuseau horaire</label>
                      <input
                        id="event-timezone"
                        value={form.timezone}
                        onChange={(e) => setField("timezone", e.target.value)}
                        placeholder="Europe/Paris"
                      />
                    </Field>

                    <Field>
                      <label htmlFor="event-max-participants">Nombre de places maximum</label>
                      <input
                        id="event-max-participants"
                        type="number"
                        min="1"
                        value={form.max_participants}
                        onChange={(e) =>
                          setField("max_participants", e.target.value)
                        }
                        placeholder="30"
                      />
                    </Field>

                    <Field $full>
                      <label htmlFor="event-recurrence-rule">Récurrence RRULE</label>
                      <input
                        id="event-recurrence-rule"
                        value={form.recurrence_rule}
                        onChange={(e) =>
                          setField("recurrence_rule", e.target.value)
                        }
                        placeholder="RRULE:FREQ=WEEKLY;BYDAY=MO"
                      />
                    </Field>

                    <Field>
                      <label htmlFor="event-category">Catégorie</label>
                      <select
                        id="event-category"
                        value={form.event_category}
                        onChange={(e) =>
                          setField("event_category", e.target.value)
                        }
                      >
                        {EVENT_CATEGORY_VALUES.map((value) => (
                          <option key={value} value={value}>
                            {EVENT_CATEGORY_PRESENTATION[value].label}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field>
                      <label htmlFor="event-conference-provider">Visio</label>
                      <select
                        id="event-conference-provider"
                        value={form.conference_provider}
                        onChange={(e) =>
                          setField("conference_provider", e.target.value)
                        }
                      >
                        <option value="">Aucune</option>
                        <option value="zoom">Zoom</option>
                        <option value="teams">Microsoft Teams</option>
                        <option value="google_meet">Google Meet</option>
                      </select>
                    </Field>
                  </FieldsGrid>
                </AdminSection>

                <AdminSection>
                  <AdminSectionHeader>
                    <AdminSectionBadge>Paiement</AdminSectionBadge>
                    <AdminSectionTitle>Inscription payante</AdminSectionTitle>
                  </AdminSectionHeader>

                  <CheckRow>
                    <input
                      type="checkbox"
                      checked={form.payment_required}
                      onChange={(e) =>
                        setField("payment_required", e.target.checked)
                      }
                    />
                    <span>Paiement requis pour participer</span>
                  </CheckRow>

                  {form.payment_required && (
                    <>
                      <InlineInfo>
                        Le paiement en ligne peut être relié plus tard à Stripe,
                        PayPal ou CAWL. En attendant, tu peux renseigner une URL
                        de paiement temporaire.
                      </InlineInfo>

                      <FieldsGrid>
                        <Field>
                          <label htmlFor="event-payment-amount">Montant TTC</label>
                          <input
                            id="event-payment-amount"
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.payment_amount_ttc}
                            onChange={(e) =>
                              setField("payment_amount_ttc", e.target.value)
                            }
                            placeholder="10.00"
                          />
                        </Field>

                        <Field>
                          <label htmlFor="event-payment-currency">Devise</label>
                          <input
                            id="event-payment-currency"
                            value={form.payment_currency}
                            onChange={(e) =>
                              setField("payment_currency", e.target.value)
                            }
                            maxLength={3}
                            placeholder="EUR"
                          />
                        </Field>

                        <Field>
                          <label htmlFor="event-payment-provider">Prestataire</label>
                          <select
                            id="event-payment-provider"
                            value={form.payment_provider}
                            onChange={(e) =>
                              setField("payment_provider", e.target.value)
                            }
                          >
                            <option value="">À configurer</option>
                            <option value="stripe">Stripe</option>
                            <option value="paypal">PayPal</option>
                            <option value="cawl">CAWL</option>
                            <option value="cb">Carte bancaire</option>
                          </select>
                        </Field>

                        <Field $full>
                          <label htmlFor="event-payment-url">URL de paiement</label>
                          <input
                            id="event-payment-url"
                            type="url"
                            value={form.payment_url}
                            onChange={(e) =>
                              setField("payment_url", e.target.value)
                            }
                            placeholder="https://..."
                          />
                        </Field>
                      </FieldsGrid>
                    </>
                  )}
                </AdminSection>

                <EditorActions>
                  <EditorActionButton type="submit">
                    {isEditMode ? "Enregistrer" : "Créer"}
                  </EditorActionButton>

                  <EditorActionButton
                    type="button"
                    variant="ghost"
                    onClick={() => navigate("/calendar")}
                  >
                    Annuler
                  </EditorActionButton>

                  {isEditMode && (
                    <EditorActionButton
                      type="button"
                      variant="danger"
                      onClick={handleDeleteEvent}
                    >
                      Supprimer
                    </EditorActionButton>
                  )}
                </EditorActions>
              </LeftColumn>

              <RightColumn>
                <AdminSection>
                  <AdminSectionHeader>
                    <AdminSectionBadge>Participants</AdminSectionBadge>
                    <AdminSectionTitle>Invitations</AdminSectionTitle>
                  </AdminSectionHeader>

                  <FieldsGrid>
                    <Field $full>
                      <label htmlFor="event-participant-email">Email participant</label>
                      <input
                        id="event-participant-email"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddParticipant();
                          }
                        }}
                        placeholder="personne@mail.fr"
                      />
                    </Field>

                    <InlineAdminButton type="button" onClick={handleAddParticipant}>
                      Ajouter le participant
                    </InlineAdminButton>
                  </FieldsGrid>

                  <ParticipantList>
                    {participants.length === 0 ? (
                      <EmptyText>Aucun participant ajouté.</EmptyText>
                    ) : (
                      participants.map((participant, index) => (
                        <ParticipantRow key={`${participant.name}-${index}`}>
                          <div>
                            <strong>{participant.name}</strong>
                            <small>
                              {participant.mailed
                                ? "Invitation envoyée"
                                : "Invitation non envoyée"}
                            </small>
                          </div>

                          <RemoveButton
                            type="button"
                            onClick={() => handleRemoveParticipant(index)}
                          >
                            ×
                          </RemoveButton>
                        </ParticipantRow>
                      ))
                    )}
                  </ParticipantList>

                  <EventRegistrationEmailSender
                    eventUuid={effectiveUuid}
                    ensureEventUuid={ensureEventUuid}
                    onEventUuidResolved={(resolvedUuid) =>
                      setCreatedUuid(resolvedUuid)
                    }
                    eventTitle={form.title}
                    defaultRecipients={nonInvitedEmails}
                    onSent={markMailed}
                    trigger={
                      <InlineAdminButton
                        type="button"
                        disabled={!nonInvitedEmails.length || !form.title.trim()}
                        title={
                          !form.title.trim()
                            ? "Ajoute d’abord un titre à l’événement."
                            : !nonInvitedEmails.length
                            ? "Aucun participant non invité."
                            : ""
                        }
                      >
                        Envoyer un mail
                        <Icon src={envelopeIcon} alt="" />
                      </InlineAdminButton>
                    }
                  />
                </AdminSection>

                <AdminSection>
                  <AdminSectionHeader>
                    <AdminSectionBadge>Résumé</AdminSectionBadge>
                    <AdminSectionTitle>Informations publiques</AdminSectionTitle>
                  </AdminSectionHeader>

                  <SummaryList>
                    <span>Titre : {form.title || "Non renseigné"}</span>
                    <span>Lieu : {form.address || "Non renseigné"}</span>
                    <span>
                      Places : {participants.length}
                      {form.max_participants ? `/${form.max_participants}` : ""}
                    </span>
                    <span>
                      Paiement :{" "}
                      {form.payment_required
                        ? `${form.payment_amount_ttc || "montant à confirmer"} ${
                            form.payment_currency || "EUR"
                          }`
                        : "Non requis"}
                    </span>
                  </SummaryList>
                </AdminSection>
              </RightColumn>
            </MainGrid>
      </FormWrapper>
    </EditorWorkspace>
  );
}

export default EditEvent;

const EditorWorkspace = styled.div`
  width: min(100%, 1180px);
  margin: 0 auto;
  padding: 1rem;
  box-sizing: border-box;
  display: grid;
  gap: 0.95rem;
  color: #f7f7f7;
`;

const HeaderCopy = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const HeaderDescription = styled.p`
  margin: 0;
  opacity: 0.76;
  line-height: 1.45;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(320px, 0.8fr);
  gap: 0.95rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: grid;
  gap: 0.95rem;
`;

const RightColumn = styled.div`
  display: grid;
  gap: 0.95rem;
  align-content: start;
`;

const FormWrapper = styled.form`
  display: grid;
  gap: 0.95rem;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input,
  select,
  textarea {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  textarea {
    min-height: 120px;
    resize: vertical;
    font-family: inherit;
  }

  input:focus,
  select:focus,
  textarea:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-height: 42px;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-size: 0.86rem;
  font-weight: 850;
  cursor: pointer;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: #fff9ea;
  }
`;

const InlineInfo = styled.div`
  margin: 0.85rem 0;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-size: 0.85rem;
  font-weight: 750;
  line-height: 1.4;
`;

const EditorActions = styled(AdminActions)`
  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const EditorActionButton = styled(AdminButton)`
  flex: 1;
`;

const InlineAdminButton = styled(AdminButton)`
  width: 100%;
  justify-content: center;
`;

const ParticipantList = styled.div`
  display: grid;
  gap: 0.55rem;
  margin-top: 0.9rem;
  max-height: 280px;
  overflow-y: auto;
`;

const ParticipantRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.1);

  strong {
    display: block;
    font-size: 0.86rem;
  }

  small {
    display: block;
    margin-top: 0.18rem;
    opacity: 0.7;
    font-size: 0.75rem;
  }
`;

const RemoveButton = styled(AdminButton)`
  border: none;
  border-radius: 999px;
  width: 30px;
  height: 30px;
  background: rgba(180, 40, 40, 0.9);
  color: white;
  font-weight: 950;
  cursor: pointer;
`;

const SummaryList = styled.div`
  display: grid;
  gap: 0.45rem;
  font-size: 0.88rem;
  opacity: 0.84;
`;

const EmptyText = styled.div`
  padding: 0.85rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.08);
  opacity: 0.78;
`;

const Icon = styled.img`
  width: 16px;
  height: 16px;
  filter: brightness(0);
`;
